import { SpecificationFormRuleFields } from './specification-form-rule';

/**
 * Normative mapping between the three names a single field carries.
 *
 * The rule key, the form control name and the `Project` property diverge for two fields, and that
 * divergence has already caused defects — `providerOwnedFields` must carry `'state'`, never
 * `'region'`, because it is typed against the rule keys.
 *
 * | Rule key      | Form control       | Project property     |
 * | ------------- | ------------------ | -------------------- |
 * | `state`       | `regionControl`    | `region`             |
 * | `currency`    | `currencyControl`  | `alternateCurrency`  |
 * | everything else | `<key>Control`   | same as the key      |
 */
export type RuleFieldKey = keyof SpecificationFormRuleFields;

const CONTROL_NAME_OVERRIDES: Partial<Record<RuleFieldKey, string>> = {
  state: 'regionControl'
};

const PROJECT_PROPERTY_OVERRIDES: Partial<Record<RuleFieldKey, string>> = {
  state: 'region',
  currency: 'alternateCurrency'
};

/**
 * Rule keys that `createForm` actually backs with a form control.
 *
 * NOT every rule field is a control. `map` is a display-only flag gating the "Check location on
 * Google Maps" link, and `createForm` has no branch for it — so anything that walks the rule and
 * creates controls must consult this set, or it invents a phantom `mapControl` and the group no
 * longer matches a freshly-built one.
 */
export const CONTROL_BACKED_FIELDS: ReadonlySet<RuleFieldKey> = new Set<RuleFieldKey>([
  'name', 'description', 'postalCode', 'address1', 'address2', 'city', 'state', 'country',
  'currency', 'exchangeRate', 'cod', 'tenderProject', 'status', 'latitude', 'longitude',
  'program', 'projectType'
]);

/** True when the rule key corresponds to a real form control. */
export const isControlBacked = (key: string): boolean =>
  CONTROL_BACKED_FIELDS.has(key as RuleFieldKey);

/** The reactive-form control name for a rule key. */
export const controlNameFor = (key: RuleFieldKey): string =>
  CONTROL_NAME_OVERRIDES[key] ?? `${key}Control`;

/** The `Project` property name for a rule key. */
export const projectPropertyFor = (key: RuleFieldKey): string =>
  PROJECT_PROPERTY_OVERRIDES[key] ?? key;

/**
 * Reverse lookup: `Project` property -> form control name.
 *
 * Derived from the same two override tables rather than written out again, so the `state`/`region`
 * and `currency`/`alternateCurrency` divergences cannot drift apart. An external prefill patch is
 * keyed by `Project` property (that is the server's vocabulary), but it has to be applied to form
 * CONTROLS, and those are the two names that differ.
 */
const CONTROL_NAME_BY_PROJECT_PROPERTY: ReadonlyMap<string, string> = new Map(
  [...CONTROL_BACKED_FIELDS].map(key => [projectPropertyFor(key), controlNameFor(key)])
);

/** The form control backing a `Project` property, or undefined when nothing does. */
export const controlNameForProjectProperty = (property: string): string | undefined =>
  CONTROL_NAME_BY_PROJECT_PROPERTY.get(property);
